"use client";

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Trash2, UserPlus, ArrowRight, Search, Upload, ArrowUpDown,
  ChevronUp, ChevronDown, Settings, Loader2, Download,
  FileText, FileSpreadsheet, Eye,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";

import CreateStudentModal from "./CreateStudentModal";
import EditProfileModal from "./EditProfileModal";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import ImportSuccessDialog from "./ImportSuccessDialog";
import ImportProgressDialog from "./ImportProgressDialog";
import ImportManualDialog from "./StudentImportManual";
import StudentProfile from "./StudentProfile";
import Pagination from "../../Common/Pagination";
import { getFullName, getInitial } from "../../../utils/urlUtils";
import { useStudents } from "./hooks/useStudents";
import { useStudentExport } from "./hooks/useStudentExport";

function StudentsContent() {
  const navigate = useNavigate();
  const {
    students, isLoading, error, setError,
    totalStudents, selectedClass, setSelectedClass,
    statusFilter, setStatusFilter,
    searchQuery, setSearchQuery, debouncedSearchQuery,
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage,
    sortColumn, setSortColumn, sortDirection, setSortDirection,
    filteredAcademicYear, setFilteredAcademicYear,
    academicYears, classes, sections, studentStatusCounts,
    selectedStudentProfile, setSelectedStudentProfile,
    selectedStudent, setSelectedStudent,
    studentRouteName,
    studentSubjects, loadingSubjects,
    activeTab, setActiveTab,
    isEditModalOpen, setIsEditModalOpen,
    isModalOpen, setIsModalOpen,
    isDeleteDialogOpen, setIsDeleteDialogOpen,
    isImporting, importProgress, showProgressDialog,
    importSuccess, setImportSuccess,
    successMessage,
    isImportManualOpen, setIsImportManualOpen,
    fetchStudents, handleUpdateStudent, handleDeleteStudent,
    handleViewProfile, handleFileUpload, getFilteredStudents,
    fetchAllStudentsForExport,
  } = useStudents();

  const {
    exportColumns, setExportColumns,
    previewData, isPreviewOpen, setIsPreviewOpen,
    isColumnSelectionOpen, setIsColumnSelectionOpen,
    handleExportCSV, handleExportExcel, handleExportPDF, handlePreviewExport,
    resetExportColumns, selectAllColumns, deselectAllColumns,
  } = useStudentExport({ fetchAllStudentsForExport });

  return (
    <div className="space-y-4">
      {selectedStudentProfile ? (
        <div className="w-full">
          <Button
            onClick={() => {
              setSelectedStudentProfile(null);
              navigate("/dashboard/students");
            }}
            className="mb-4 bg-gray-500 hover:bg-gray-600 text-white"
          >
            Back to Student Management
          </Button>
          <StudentProfile
            student={selectedStudentProfile}
            studentRouteName={studentRouteName}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            studentSubjects={studentSubjects}
            loadingSubjects={loadingSubjects}
            onEdit={() => { setSelectedStudent(selectedStudentProfile); setIsEditModalOpen(true); }}
            onDelete={() => setIsDeleteDialogOpen(true)}
            onRefresh={fetchStudents}
            onError={setError}
          />
        </div>
      ) : (
        <>
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-[#0263cc] text-xl font-semibold">Student Management</CardTitle>
                  <CardDescription>View and manage student information</CardDescription>
                </div>
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                  {/* Search */}
                  <div className="relative w-full sm:w-auto">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search students..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                    />
                  </div>

                  {/* Sort */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-10 h-10 p-0 rounded-full text-[#0263cc] hover:text-[#0263cc] hover:border-[#0263cc] border-[#0263cc]/20" title="Sort by name">
                        <ArrowUpDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuRadioGroup
                        value={sortDirection === "asc" ? "aToZ" : "zToA"}
                        onValueChange={(value) => {
                          setSortColumn("name");
                          setSortDirection(value === "aToZ" ? "asc" : "desc");
                        }}
                      >
                        <DropdownMenuRadioItem value="aToZ">A to Z</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="zToA">Z to A</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Export */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-10 h-10 p-0 rounded-full text-red-600 hover:text-red-600 hover:border-red-600 border-[#0263cc]/20" title="Export">
                        <Download className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuItem onClick={() => setIsColumnSelectionOpen(true)}>
                        <Settings className="w-4 h-4 mr-2" /> Select Columns
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleExportCSV}>
                        <FileText className="w-4 h-4 mr-2" /> Export as CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportExcel("xlsx")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xlsx
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportExcel("xls")}>
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xls
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handlePreviewExport}>
                        <Eye className="w-4 h-4 mr-2" /> Preview Export
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Import */}
                  <div className="relative">
                    <button
                      className={`p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 ${isImporting ? "opacity-50 cursor-not-allowed" : ""}`}
                      disabled={isImporting}
                      onClick={() => setIsImportManualOpen(true)}
                      title={isImporting ? "Importing..." : "Import Students"}
                    >
                      <Upload className="w-5 h-5" />
                    </button>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={isImporting}
                    />
                    {isImporting && (
                      <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-blue-500 animate-pulse"></span>
                    )}
                  </div>

                  {/* Add Student */}
                  <Button
                    onClick={() => { setSelectedStudent(null); setIsModalOpen(true); }}
                    className="w-10 h-10 p-0 rounded-full bg-green-500 hover:bg-green-600 transition-colors duration-200"
                    title="Add New Student"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {/* Filters */}
              <div className="p-4 bg-white border-b border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="academic-year" className="text-sm font-medium text-gray-600 mb-1.5 block">Academic Year</Label>
                    <Select value={filteredAcademicYear} onValueChange={setFilteredAcademicYear}>
                      <SelectTrigger id="academic-year" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Select Academic Year" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        {academicYears.map((year) => (
                          <SelectItem key={year._id} value={year._id}>
                            {year.startYear}-{year.endYear}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="class-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Filter by Class</Label>
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger id="class-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Filter by Class" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="all">All Classes</SelectItem>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {classes.map((cls) => (
                          <SelectItem key={cls.name} value={cls.name}>Class {cls.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="status-filter" className="text-sm font-medium text-gray-600 mb-1.5 block">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger id="status-filter" className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="active">Active ({studentStatusCounts.active})</SelectItem>
                        <SelectItem value="inactive">Inactive ({studentStatusCounts.inactive})</SelectItem>
                        <SelectItem value="alumni">Alumni ({studentStatusCounts.alumni})</SelectItem>
                        <SelectItem value="all">
                          All ({studentStatusCounts.active + studentStatusCounts.inactive + studentStatusCounts.alumni})
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {error && (
                <Alert variant="destructive" className="m-4 rounded-lg bg-red-50 text-red-800 border-red-200">
                  <AlertDescription className="font-medium">{error}</AlertDescription>
                </Alert>
              )}

              {successMessage && (
                <Alert className="m-4 rounded-lg bg-green-50 text-green-800 border-green-200">
                  <AlertDescription className="font-medium">{successMessage}</AlertDescription>
                </Alert>
              )}

              <ImportProgressDialog
                isOpen={showProgressDialog}
                progress={importProgress}
                totalRecords={importProgress.totalRecords}
                processedRecords={importProgress.processedRecords}
                successCount={importProgress.successCount}
                failureCount={importProgress.failureCount}
              />

              <ImportSuccessDialog
                isOpen={importSuccess}
                onClose={() => setImportSuccess(false)}
                imported={importProgress.successCount}
                failed={importProgress.failureCount}
                total={importProgress.totalRecords}
              />

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-y border-gray-200">
                      <th
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          setSortColumn("name");
                          setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                        }}
                      >
                        <div className="flex items-center">
                          Student Name
                          {sortColumn === "name" ? (
                            sortDirection === "asc"
                              ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
                              : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Admission No.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Roll No.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Class</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Section</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-[#0263cc]" />
                            <span className="text-sm">Loading students...</span>
                          </div>
                        </td>
                      </tr>
                    ) : getFilteredStudents().length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          {debouncedSearchQuery ? "No students found matching your search" : "No students available"}
                        </td>
                      </tr>
                    ) : (
                      getFilteredStudents().map((student) => (
                        <tr
                          key={student._id}
                          className="hover:bg-gray-50 transition-colors"
                          onClick={() => handleViewProfile(student)}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                                {student.profileImage ? (
                                  <img src={student.profileImage || "/placeholder.svg"} alt={getFullName(student)} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-blue-600 font-semibold">{getInitial(student)}</span>
                                )}
                              </div>
                              <span className="font-medium text-[#0263cc] hover:text-[#0263cc]/80">
                                {student.firstName
                                  ? [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ")
                                  : student.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">{student.admissionNumber}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{student.classMapping?.rollNumber || "Not Set"}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{student.classMapping?.class?.name || "Unassigned"}</td>
                          <td className="px-4 py-4 text-sm text-gray-600">{student.classMapping?.section?.name || "Unassigned"}</td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${
                              (student.status || "active") === "active" ? "bg-green-100 text-green-800"
                              : student.status === "inactive" ? "bg-red-100 text-red-800"
                              : student.status === "alumni" ? "bg-amber-100 text-amber-800"
                              : "bg-gray-100 text-gray-800"
                            }`}>
                              {student.status || "active"}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-600 hover:text-[#0263cc]"
                              onClick={(e) => { e.stopPropagation(); handleViewProfile(student); }}
                            >
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalItems={totalStudents}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                currentPageCount={getFilteredStudents().length}
                itemLabel="students"
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Modals */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedStudent(null); }}
        student={selectedStudent}
        onUpdate={handleUpdateStudent}
      />
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => {
          handleDeleteStudent(selectedStudentProfile._id);
          setSelectedStudentProfile(null);
        }}
      />
      <CreateStudentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentAcademicYear={academicYears.find((year) => year.isCurrent) || null}
        classes={classes}
        sections={sections}
        onSuccess={fetchStudents}
      />

      {/* Column Selection Dialog */}
      <Dialog open={isColumnSelectionOpen} onOpenChange={setIsColumnSelectionOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Export Columns</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllColumns}>Select All</Button>
              <Button variant="outline" size="sm" onClick={deselectAllColumns}>Deselect All</Button>
              <Button variant="outline" size="sm" onClick={resetExportColumns}>Reset to Default</Button>
            </div>

            {[
              {
                title: "Basic Information",
                fields: [
                  { id: "name", label: "Full Name" }, { id: "firstName", label: "First Name" },
                  { id: "middleName", label: "Middle Name" }, { id: "lastName", label: "Last Name" },
                  { id: "username", label: "Username" }, { id: "admissionNumber", label: "Admission Number" },
                  { id: "aadhaarCard", label: "Aadhaar Card" }, { id: "email", label: "Email" },
                  { id: "phoneNumber", label: "Phone Number" }, { id: "birthday", label: "Birthday" },
                  { id: "bloodGroup", label: "Blood Group" }, { id: "gender", label: "Gender" },
                ],
              },
              {
                title: "Academic Information",
                fields: [
                  { id: "class", label: "Class" }, { id: "section", label: "Section" },
                  { id: "rollNumber", label: "Roll Number" }, { id: "status", label: "Status" },
                ],
              },
              {
                title: "Parent/Guardian Information",
                fields: [
                  { id: "fatherName", label: "Father's Name" }, { id: "fatherContactNumber", label: "Father's Contact" },
                  { id: "motherName", label: "Mother's Name" }, { id: "motherContactNumber", label: "Mother's Contact" },
                  { id: "guardianName", label: "Guardian's Name" }, { id: "guardianContactNumber", label: "Guardian's Contact" },
                ],
              },
              {
                title: "Address Information",
                fields: [
                  { id: "address", label: "Full Address" }, { id: "addressStreet", label: "Street" },
                  { id: "addressCity", label: "City" }, { id: "addressState", label: "State" },
                  { id: "addressPincode", label: "Pincode" }, { id: "addressCountry", label: "Country" },
                ],
              },
              {
                title: "Transportation Information",
                fields: [
                  { id: "transportationType", label: "Transportation Type" },
                  { id: "busRoute", label: "Bus Route" },
                ],
              },
              {
                title: "System Information",
                fields: [
                  { id: "profileImage", label: "Profile Image URL" },
                  { id: "createdAt", label: "Created At" },
                  { id: "updatedAt", label: "Updated At" },
                ],
              },
            ].map(({ title, fields }) => (
              <div key={title} className="space-y-3">
                <h3 className="font-semibold text-sm text-gray-700 border-b pb-2">{title}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {fields.map(({ id, label }) => (
                    <div key={id} className="flex items-center space-x-2">
                      <Checkbox
                        id={id}
                        checked={exportColumns[id]}
                        onCheckedChange={(checked) => setExportColumns({ ...exportColumns, [id]: checked })}
                      />
                      <label htmlFor={id} className="text-sm cursor-pointer">{label}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center pt-4 border-t mt-6">
            <p className="text-sm text-gray-600">
              {Object.keys(exportColumns).filter((key) => exportColumns[key]).length} column(s) selected
            </p>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => setIsColumnSelectionOpen(false)}>Cancel</Button>
              <Button onClick={() => { setIsColumnSelectionOpen(false); handlePreviewExport(); }}>
                Preview with Selected Columns
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Export Preview</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto overflow-y-auto flex-1 max-h-[calc(90vh-180px)]">
            {previewData && (
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    {Object.keys(exportColumns)
                      .filter((key) => exportColumns[key])
                      .map((key) => (
                        <TableHead key={key} className="whitespace-nowrap bg-gray-50">
                          {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                        </TableHead>
                      ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.map((row, index) => (
                    <TableRow key={index}>
                      {Object.keys(exportColumns)
                        .filter((key) => exportColumns[key])
                        .map((key) => (
                          <TableCell key={key} className="whitespace-nowrap max-w-[200px] overflow-hidden text-ellipsis">
                            {row[key]}
                          </TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-sm text-gray-500">
              Showing preview of first 5 records • {Object.keys(exportColumns).filter((key) => exportColumns[key]).length} columns selected
            </p>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button><Download className="w-4 h-4 mr-2" /> Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportCSV}>
                    <FileText className="w-4 h-4 mr-2" /> Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportExcel("xlsx")}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xlsx
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExportExcel("xls")}>
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Export as xls
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImportManualDialog
        isOpen={isImportManualOpen}
        onClose={() => setIsImportManualOpen(false)}
      />
    </div>
  );
}

export default StudentsContent;
